import type { IDatapoint } from '@/interfaces/IDatapoint'

// Enum definitions for fixed sets of values
export enum MessageType {
  Fault = 'fault',
  Service = 'service',
  Alarm = 'alarm',
  Status = 'status',
  Warning = 'warning',
}

export enum Direction {
  Read = 'r',
  Write = 'w',
}

export enum Orientation {
  Vertical = 'vertical',
  Horizontal = 'horizontal',
}

export enum ScreenItemType {
  Screenjump = 'screenjump',
  Separator = 'separator',
  Seperator = 'seperator', // support typo from JSON
  Extend = 'extend',
}

// User interface
export interface IUser {
  readonly name: string
  readonly level: number
  readonly last_login: number
}

// Main project interface – using readonly where possible
export interface IProject {
  readonly version: string
  readonly timezone: string // (e.g., 'Europe/Berlin', 'UTC')
  readonly time: number // UNIX timestamp in ms
  readonly hwid: string
  readonly uptime: number
  readonly type: string
  readonly users: ReadonlyArray<IUser>
  readonly profile: IProfile
  readonly config: IConfig
  readonly wizard: string
  /**
   * Capabilities the firmware announces. Absent on devices that predate the
   * feature list — see supportsAttractionEngine() in useMqttConnection.
   */
  readonly features?: ReadonlyArray<string>
}

export interface IProfile {
  readonly customerName: string
  readonly customerStreet: string
  readonly customerZip: string
  readonly customerCity: string
  readonly customerCountry: string
  readonly customerTel: string
  readonly customerEmail: string
  readonly poolSize: string
  readonly poolVolume: number
  readonly poolCover: boolean
  readonly poolLocation: string
  readonly poolType: string
  readonly dealerColor: string
  readonly dealerName: string
  readonly dealerStreet: string
  readonly dealerZIP: string
  readonly dealerCity: string
  readonly dealerCountry: string
  readonly dealerTel: string
  readonly dealerEmail: string
  readonly dealerWebsite: string
  readonly type: string
}

// Global configuration interface
export interface IConfig {
  readonly general: IGeneralConfig
  readonly interfaces: IInterfaces
  readonly systems: ReadonlyArray<ISystem>
  readonly messages: ReadonlyArray<IMessageConfig>
  readonly datapoints: Record<string, IDatapoint>
  readonly logics: Record<string, ILogicConfig>
  readonly frontend: IFrontend
  /** Message lists of their own, beside the circuits (e.g. Bluewave on a Yellow). */
  readonly messagelists?: ReadonlyArray<IMessageListConfig>
}

export interface IMessageListConfig {
  readonly name: string
  readonly label: string
}

// General configuration
export interface IGeneralConfig {
  readonly application: string
  readonly projectName: string
  readonly editor: string
  readonly timestamp: number
  readonly weather: boolean
  readonly nfc: boolean
  readonly tutorial: boolean
}

// Interfaces configuration for different communication channels
export interface IInterfaces {
  readonly mqtt: IMQTTInterface
  readonly p20: IP20Interface
}

export interface IMQTTInterface {
  readonly protocol: string
  readonly mapping: ReadonlyArray<IMQTTMapping>
}

export interface IMQTTMapping {
  readonly datapoint: string
  readonly dir: Direction
}

export interface IP20Interface {
  readonly protocol: string
  readonly interfaceType: string // renamed from 'interface' since it's a reserved word
  readonly enable: boolean
  readonly port: string
  readonly queueSpeed: string
  readonly timeout: string
  readonly baud: number
  readonly devices: ReadonlyArray<IP20Device>
}

export interface IP20Device {
  readonly name: string
  readonly address: number
  readonly universe?: number
  readonly mapping: ReadonlyArray<IP20Mapping>
}

export interface IP20Mapping {
  readonly name: string
  readonly register: number
  readonly datapoint?: string
  readonly interval: number
  readonly dir: Direction
  readonly scale?: number
  readonly value?: number
  readonly bit?: number
}

// System definition
/** One circuit of the plant: a basin with its own menu, start screen and messages. */
export interface ISystem {
  readonly name: string
  readonly label: string
  readonly type: string
  readonly error: string
  readonly sidebar: string
  readonly startscreen: string
  /** First segment of the circuit's screen keys; missing means `name`. */
  readonly prefix?: string
  /** Datapoints the plant overview shows for this circuit. */
  readonly overview?: ReadonlyArray<string>
  readonly icon?: string
  readonly theme?: string
}

/** One datapoint a reset writes to. `value` defaults to `true` when absent. */
export interface IResetTarget {
  readonly datapoint: string
  readonly value?: string | number | boolean
}

/**
 * The three forms a message reset takes in the wild — a bare datapoint name
 * (writes `true`), a single target with its own value, or several of either.
 * The fleet corpus sees both `string` and `object` on `messages[].reset`.
 */
export type MessageReset = string | IResetTarget | ReadonlyArray<string | IResetTarget>

// Message configuration interface
export interface IMessageConfig {
  readonly text: string
  readonly datapoint: string
  readonly type: MessageType
  readonly description: string
  readonly solution?: string
  readonly screen?: string
  readonly reset?: MessageReset
  readonly system: string
  readonly dptext?: string | ReadonlyArray<string>
  readonly fixtext?: string | ReadonlyArray<string | number>
  readonly start?: number
  readonly end?: number
}

export interface IArchivedMessageConfig {
  readonly type: MessageType
  readonly text: string
  readonly start: number
  readonly end: number
  /** The circuit the archive was asked for; the device's answer does not carry it. */
  readonly system?: string
}

// Logic configuration interface – using an index signature for additional properties
export interface ILogicConfig {
  readonly type: string

  [key: string]: unknown
}

// Frontend configuration – contains sidebars and screens
export interface IFrontend {
  readonly general?: IFrontendGeneral
  readonly sidebars: Record<string, ReadonlyArray<ISidebarItem>>
  readonly screens: Record<string, IScreen>
}

export interface IFrontendGeneral {
  readonly theme?: string
}

// Base interface for remote control
export interface IRemoteControl {
  readonly remote?: boolean // Controls if item should be shown/accessible remotely
}

// Sidebar item
export interface ISidebarItem extends IRemoteControl {
  readonly type: ScreenItemType
  readonly label?: string // not needed for "separator"/"seperator"
  readonly icon?: string // not needed for "separator"/"seperator"
  readonly screen?: string // only for "screenjump"
  readonly items?: ReadonlyArray<ISidebarItem> // only for "extend"
}

// Base interface for all screens
export interface IScreenBase extends IRemoteControl {
  readonly name: string
  readonly title: string
}

// Screen union type
export type IScreen =
  | IViewPagerScreen
  | IChipsConfigScreen
  | ITinyParameterPageScreen
  | IParameterPageScreen
  | IListViewScreen
  | ITimerScreen
  | IEventTimerScreen
  | IDMXScreen
  | IPhCalibrationScreen
  | IRedoxCalibrationScreen
  | IPotCalibrationScreen
  | IAttractionsScreen
  | ITrendScreen
  | IWebviewScreen

export interface IViewPagerScreen extends IScreenBase {
  readonly type: 'viewPager'
  readonly startIndex: number
  readonly orientation: Orientation
  readonly animatedBackground: boolean
  readonly pages: ReadonlyArray<string>
}

export interface IChipsConfigScreen extends IScreenBase {
  readonly type: 'chips'
  readonly chips: ReadonlyArray<IChipConfig>
}

export interface ITinyParameterPageScreen extends IScreenBase {
  readonly type: 'tinyparameterPage'
  readonly cards: ReadonlyArray<ICard>
}

/** The same cards as a tinyparameterPage, shown with the value as text instead of a gauge. */
export interface IParameterPageScreen extends IScreenBase {
  readonly type: 'parameterPage'
  readonly cards: ReadonlyArray<ICard>
}

export interface IListViewScreen extends IScreenBase {
  readonly type: 'listview'
  readonly content: ReadonlyArray<IListViewCard>
  readonly extra?: string
}

export interface ITimerScreen extends IScreenBase {
  readonly type: 'timer'
  readonly mode?: 'onOff' | 'interval'
  readonly datapoint: string
  /** Optional: absent on 6 of 36 timer screens in the fleet. */
  readonly default?: string
  readonly valueDatapoint?: string
}

export interface IDMXScreen extends IScreenBase {
  readonly type: 'dmx'
  /** Datapoint gating the DMX output. Every dmx screen in the fleet uses this. */
  readonly enable: string
  /** Legacy spelling; no device in the fleet ships it. */
  readonly active?: string
}

export interface IPhCalibrationScreen extends IScreenBase {
  readonly type: 'phCalibration'
  readonly actualValue: string
  readonly inputVoltage: string
  readonly slope: string
  readonly zeropoint: string
  readonly buffer1Voltage: string
  readonly buffer1Value: string
  readonly buffer2Voltage: string
  readonly buffer2Value: string
  readonly calibrationDone: string
}

export interface IRedoxCalibrationScreen extends IScreenBase {
  readonly type: 'redoxCalibration'
  readonly actualValue: string
  readonly inputVoltage: string
  readonly buffer1Voltage: string
  readonly buffer1Value: string
}

export interface IPotCalibrationScreen extends IScreenBase {
  readonly type: 'potCalibration'
  readonly calibrateVoltage: string
  readonly calibrateValue: string
  readonly actualValue: string
  readonly inputVoltage: string
  readonly slope: string
  /** Optional: absent on 1 of 4 potCalibration screens in the fleet. */
  readonly flowValue?: string
  readonly calibrationDone?: string
}

export interface IEventTimerScreen extends IScreenBase {
  readonly type: 'eventtimer'
  readonly datapoint: string
  /** Present on every eventtimer screen in the fleet, but only 4 were seen. */
  readonly default?: string
  /** Optional: no device in the fleet ships this. */
  readonly valueDatapoint?: string
  /** Optional: no device in the fleet ships this. */
  readonly weekinterval?: boolean
}

export interface IAttractionsScreen extends IScreenBase {
  readonly type: 'attractions'
  readonly attractions: ReadonlyArray<IAttraction>
}

// New trend screen
export interface ITrendScreen extends IScreenBase {
  readonly type: 'trend'
  readonly pens: ReadonlyArray<ITrendPen>
}

export interface ITrendPen {
  readonly label: string
  readonly datapoint: string
  readonly color: string
  readonly min?: string
  readonly max?: string
}

// New webview screen
export interface IWebviewScreen extends IScreenBase {
  readonly type: 'webview'
  readonly file: string
}

// List view card configuration
export interface IListViewCard extends IRemoteControl {
  readonly type: 'card'
  readonly title: string
  /** Optional: present on 543 of 566 cards in the fleet. */
  readonly subtitle?: string
  /** Optional: present on only 22 of 566 cards in the fleet. */
  readonly dpSubtitle?: string
  readonly icon: string
  readonly content: ReadonlyArray<IListViewCardContentConfigType>
}

// Base interface for list view card content
export interface IListViewCardContentConfig extends IRemoteControl {
  readonly type: string
  readonly label: string
  readonly description?: string
  readonly datapoint?: string
  readonly userlevel?: number
  readonly lock?: string
  readonly readonly?: boolean
  readonly color?: string
  readonly reset?: string
}

// Specific content types (extend base interface)
export interface ISwitchConfig extends IListViewCardContentConfig {
  readonly type: 'switch'
  readonly screen?: string
  readonly states?: ReadonlyArray<ISwitchConfigState>
}

export interface ISwitchConfigState {
  readonly state: boolean | number
  readonly text?: string
  readonly icon?: string
  readonly description?: string
  readonly dptext?: string
}

export interface IInputConfig extends IListViewCardContentConfig {
  readonly type: 'input' | 'dateInput'
  readonly color?: string
}

export interface IDropdownConfig extends IListViewCardContentConfig {
  readonly type: 'dropdown'
  readonly states: ReadonlyArray<IDropdownState>
}

export interface IIconStateConfig extends IListViewCardContentConfig {
  readonly type: 'iconState'
  readonly states: ReadonlyArray<IIconState>
}

export interface IIconState {
  readonly state: boolean | number
  readonly icon: string
  readonly color?: string
  readonly description?: string
  readonly datapoint?: string
  readonly animate?: 'rotate' | 'blink' | 'pulse'
}

export interface IButtonConfig extends IListViewCardContentConfig {
  readonly type: 'button'
  readonly mode: 'push' | 'force' | 'toggle'
  readonly icon?: string
  readonly text?: string
  readonly value?: string | boolean | number
}

export interface IDropdownState {
  readonly state: number
  readonly text: string
  readonly icon?: string
  readonly color?: string
}

/*
 * The three types below ship on real devices but have no component yet, so
 * ListViewCardItem renders its "Unknown Control Type" placeholder for them.
 * Field sets are taken from the fleet corpus — see
 * src/__tests__/fleet-conformance.spec.ts, which fails if a new unhandled type
 * appears or if one of these gains a component without the list being updated.
 */

/** Sensor offset calibration. Present on most devices. */
export interface ICalibrateButtonConfig extends IListViewCardContentConfig {
  readonly type: 'calibrateButton'
  readonly id: string
  readonly icon: string
  readonly inputActualValue: string
  readonly outputActualValue: string
  readonly calibrateOutput: string
  readonly feedbackOffset: string
  readonly calibrationDone?: string
}

/**
 * Manual filter backwash. Note this one carries no `label`, so it deliberately
 * does not extend IListViewCardContentConfig.
 */
export interface IManualBackwashConfig extends IRemoteControl {
  readonly type: 'manualBackwash'
  readonly backwashDuration: string
  readonly rinseDuration: string
  readonly backwashActive: string
  readonly pumpOnOff: string
}

/** Navigates to another screen instead of driving a datapoint. */
export interface IScreenjumpConfig extends IListViewCardContentConfig {
  readonly type: 'screenjump'
  readonly screen: string
  readonly icon: string
}

// Union of allowed list view card content types
export type IListViewCardContentConfigType =
  | ISwitchConfig
  | IInputConfig
  | IDropdownConfig
  | IIconStateConfig
  | IButtonConfig
  | ICalibrateButtonConfig
  | IManualBackwashConfig
  | IScreenjumpConfig

// Chip configuration for chips screen
export interface IChipConfig {
  readonly icon: string
  readonly datapoint: string
  readonly color: string
  readonly label: string
  readonly description?: string
  readonly screen?: string
  readonly states?: ReadonlyArray<IChipState>
}

export interface IChipState {
  /** Explains the state on a long press; the chip's own description stands in without it. */
  readonly description?: string
  // readonly animate?: 'rotate' | 'blink' | 'pulse'

  readonly state: boolean | number
  readonly datapoint?: string
  readonly text?: string
  readonly icon?: string
  readonly color?: string
}

// Card interface for tiny parameter pages
export interface ICard {
  readonly datapoint: string
  readonly label: string
  readonly description: string
  /** Replaces the unit of the datapoint. */
  readonly unit?: string
  /** Opened by a tap on a card without links. */
  readonly screen?: string
  readonly error?: string
  readonly warning?: string
  readonly valueError?: string
  readonly icon1?: IIcon
  readonly icon2?: IIcon
  readonly icon3?: IIcon
  readonly icon4?: IIcon
  readonly links?: ReadonlyArray<ILink>
}

export interface IIcon {
  readonly states: ReadonlyArray<IIconState>
}

export interface ILink {
  readonly type: 'screenjump'
  readonly label: string
  readonly icon: string
  readonly screen: string
}

/**
 * How an attraction button switches its output:
 *
 *  - `onoff`       plain switch
 *  - `impulscolor` light whose colour programme steps on a ~300 ms power pulse
 *  - `push`        momentary contact, runs only while held
 *
 * Firmware without the attraction engine omits the field; treat that as
 * `onoff` (see resolveAttractionMode).
 */
export type AttractionMode = 'onoff' | 'impulscolor' | 'push'

export interface IAttraction {
  readonly icon: string
  readonly datapoint: string
  readonly color: string
  readonly label: string
  readonly mode?: AttractionMode
  readonly screen?: string
  readonly delay?: string
  /** Alternative command target; the device accepts it in place of `datapoint`. */
  readonly id?: string
}
